import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Centralized route-transition scroll-to-top handler.
//
// Behavior:
//   • On genuine pathname change (a new logical page), scroll window to top.
//   • Query-string changes are treated as same-page state → no scroll.
//   • URL contains a #hash → let the browser reach the anchor target. In an
//     SPA, the anchor element usually doesn't exist at first HTML load, so
//     we defer to the next paint and scroll manually to it via
//     `scrollIntoView` (works with or without Lenis).
//   • Browser POP navigation (native back/forward) is NOT auto-scrolled —
//     we defer to the browser's native scroll restoration so returning to a
//     page lands where the user was.
//   • Reservation wizard steps and payment return flows are same-pathname
//     transitions (state within /reserve) so they are unaffected.
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType === "POP") return;

    if (hash) {
      // Wait one frame so React has committed the new DOM before we try to
      // locate the anchor target. Retry a few times in case lazy sections
      // mount asynchronously.
      const id = hash.replace(/^#/, "");
      const tries = [50, 150, 350, 750];
      const timers = tries.map((ms) =>
        setTimeout(() => {
          const el = document.getElementById(id);
          if (!el) return;
          try {
            el.scrollIntoView({ block: "start", behavior: "auto" });
          } catch {
            const y = el.getBoundingClientRect().top + window.scrollY;
            window.scrollTo(0, y);
          }
        }, ms)
      );
      return () => timers.forEach(clearTimeout);
    }

    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, navType]);

  return null;
}
